<template>
  <FriedDropdown v-model:open="open" :main-axis-offset="8" :width="230" :close-on-action="false">
    <template #trigger>
      <ProfileDropdownButton :user="user" :entity-name="entityName" :open="open" />
    </template>
    <template #content>
      <div>
        <FriedH5 class="entities-label">{{ $t('entities') }}</FriedH5>
        <FriedSearchInput
          v-if="!showLoader && entities.length > 3"
          v-model="searchInput"
          class="entity-search-box"
          data-external="profile-dropdown-entity-search-input"
          :placeholder="$t('search')"
          :size="SearchInputSize.Small"
        >
          <template #left>
            <FriedIcon icon="search" size="small" />
          </template>
        </FriedSearchInput>
        <div v-if="showLoader" class="entity-list skeleton-container">
          <FriedSkeleton v-for="index in 4" :key="index" class="skeleton" />
        </div>
        <div
          v-else-if="entities && entities.length > 1"
          class="entity-list"
          data-external="profile-dropdown-entity-list"
        >
          <template v-if="!!filteredEntities.length">
            <FriedDropdownItem
              v-for="entityItem in filteredEntities"
              :key="entityItem.id"
              :title="entityItem.name"
              :class="['entity-item', { active: entityItem.name === entityName }]"
              :data-external="`profile-dropdown-entity-button-${entityItem.id}`"
              @click="handleSelectEntity(entityItem)"
            >
              {{ entityItem.name }}
              <template #description>{{ $t(`${entityItem.plan}-plan`) }}</template>
            </FriedDropdownItem>
          </template>
          <FriedParagraph v-else class="empty-state">{{ $t('no-results-found') }}</FriedParagraph>
        </div>
        <hr class="seperator" />
      </div>
      <template v-if="showMyDocumentsOption">
        <FriedDropdownItem
          class="dropdown-item"
          data-external="profile-dropdown-my-documents-link"
          @click="handleClickMyDocuments"
        >
          {{ $t('my_documents') }}
          <template #rightIcon>
            <FriedIcon class="icon" icon="mailbox" />
          </template>
        </FriedDropdownItem>
        <hr class="seperator" />
      </template>
      <FriedDropdownItem
        v-if="!isInactiveSubscription"
        class="dropdown-item"
        :to="{ name: PathName.SettingsUserProfile }"
        data-external="profile-dropdown-my-profile-link"
      >
        {{ $t('my-profile') }}
        <template #rightIcon>
          <FriedIcon class="icon" icon="user-shield" />
        </template>
      </FriedDropdownItem>
      <FriedDropdownItem
        v-if="userRole === UserRoleLegacy.Admin && !isInactiveSubscription"
        data-external="profile-dropdown-entity-settings-link"
        class="dropdown-item"
        :to="{ name: PathName.SettingsEntity }"
      >
        {{ $t('settings') }}
        <template #rightIcon>
          <FriedIcon class="icon" icon="setting-slider" />
        </template>
      </FriedDropdownItem>
      <FriedDropdownItem
        v-else-if="userRole === UserRoleLegacy.Admin && isInactiveSubscription"
        class="dropdown-item"
        :to="{ name: PathName.SettingsSecurity }"
        :data-external="`profile-dropdown-security-link`"
      >
        {{ $t('Security') }}
        <template #rightIcon>
          <FriedIcon class="icon" icon="lock-shield" />
        </template>
      </FriedDropdownItem>
      <hr class="seperator" />
      <FriedDropdownItem
        v-if="showSubscriptionLink"
        data-external="profile-dropdown-billing-settings-link"
        class="dropdown-item"
        :to="{ name: PathName.SettingsSubscription }"
      >
        {{ $t('subscription-and-billing') }}
      </FriedDropdownItem>
      <FriedDropdownItem
        class="dropdown-item"
        data-external="profile-dropdown-logout-button"
        @click="handleLogout"
      >
        {{ $t('log-out') }}
        <template #rightIcon>
          <FriedIcon class="icon" icon="logout" />
        </template>
      </FriedDropdownItem>
    </template>
  </FriedDropdown>
</template>

<script lang="ts" setup>
import { SearchInputSize } from '@getaccept/fried-tofu';
import type { SelectEntity } from '@getaccept/lib-shared/src/select-entity/types/select-entity';
import { UserRoleLegacy } from '@getaccept/lib-shared/src/users/types/user-role-legacy';
import { logOut } from '@getaccept/lib-shared/src/helpers/log-out.helper';
import { PathName } from '@getaccept/lib-shared/src/enums/PathNames';
import { ref, computed, watch, watchEffect } from 'vue';
import { storeToRefs } from 'pinia';
import { Feature, useFeatureStore } from '@getaccept/feature-restriction';
import { useRoute } from 'vue-router';
import { useSessionStore } from '../../session/store/session.store';
import { useEntityStore } from '../../entity/entity.store';
import { useUserStore } from '../../user/store/user.store';
import { useModulesStore } from '../../modules/store/modules.store';
import { useSubscriptionStore } from '../../subscription/store/subscription.store';
import { useMyDocumentsAuth } from '../../my-documents/composables/my-documents-auth.composable';
import ProfileDropdownButton from './ProfileDropdownButton.vue';

const emit = defineEmits<{ 'select-entity': [SelectEntity]; click: []; 'validate-md-email': [] }>();

const { features } = storeToRefs(useFeatureStore());
const { isInactiveSubscription } = storeToRefs(useSubscriptionStore());
const { isContractRoomModuleActive } = storeToRefs(useModulesStore());
const { entities, entity } = storeToRefs(useEntityStore());
const searchInput = ref('');
const { session } = useSessionStore();
const { user } = storeToRefs(useUserStore());

const entityName = computed(() => entity.value?.name);
const userRole = computed(() => session?.user_role);
const isSubEntity = computed(() => session?.is_sub_entity);

const filteredEntities = computed(() =>
  searchInput.value
    ? entities.value.filter((entity: SelectEntity) =>
        [entity.name].some((queryableProperty: string) =>
          queryableProperty.toLowerCase().includes(searchInput.value.toLowerCase())
        )
      )
    : entities.value
);

const showSubscriptionLink = computed(
  () =>
    userRole.value === UserRoleLegacy.Admin && !isSubEntity.value && !isInactiveSubscription.value
);

const handleSelectEntity = (selectedEntity: SelectEntity) => {
  searchInput.value = '';
  open.value = false;
  if (selectedEntity.id === entity.value?.id) return;
  emit('select-entity', selectedEntity);
};

const handleLogout = () => logOut();

const enableFetchMyDocumentsToken = ref(false);

const { isInvalidToken, token, redirect } = useMyDocumentsAuth(
  computed(() => user.value?.id),
  computed(() => enableFetchMyDocumentsToken.value)
);

const handleClickMyDocuments = () => {
  if (!enableFetchMyDocumentsToken.value) {
    enableFetchMyDocumentsToken.value = true;
    return;
  }
  if (token.value) {
    redirect();
    return;
  }
  emit('validate-md-email');
};

watch(isInvalidToken, () => {
  if (isInvalidToken.value) {
    emit('validate-md-email');
  }
});

const showMyDocumentsOption = computed(
  () =>
    !isInactiveSubscription.value &&
    isContractRoomModuleActive.value &&
    !features.value[Feature.MyDocuments].restricted
);

const open = ref(false);
const showLoader = ref(true);

watch(
  open,
  async () => {
    if (open.value) {
      await useEntityStore().loadEntities();
      showLoader.value = false;
    }
  },
  { once: true }
);

watchEffect(() => {
  if (open.value) {
    emit('click');
  }
});

const route = useRoute();
watch(
  () => route.name,
  () => {
    open.value = false;
  },
  { immediate: true }
);
</script>

<style lang="scss" scoped>
$search-input-height: calc(2rem + #{var(--spacing-50)});
$entity-list-max-height: 225px;

.entities-label {
  font-size: var(--label-small-font-size);
  line-height: var(--label-small-line-height);
  margin: var(--spacing-25);
  margin-bottom: var(--spacing-75);
  color: var(--text-gray);
  display: block;
}

.entity-search-box {
  margin: var(--spacing-50) var(--spacing-25);
}

.entity-list {
  max-height: $entity-list-max-height;
  overflow: auto;

  .empty-state {
    padding: var(--spacing-50);
    color: var(--text-gray);
  }

  &.skeleton-container {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-50);

    .skeleton {
      height: 3.5rem;
      width: 100%;
    }
  }

  &:not(.skeleton-container)::after {
    pointer-events: none;
    content: '';
    width: 93%;
    top: calc(#{$entity-list-max-height} + #{$search-input-height});
    position: absolute;
    left: 0;
    background: linear-gradient(
      180deg,
      rgb(255 255 255 / 20%) 50%,
      rgb(255 255 255 / 80%) 100%,
      var(--white) 100%
    );
    height: 43px;

    @media only screen and (max-width: calc(#{$sm} - 1px)) {
      top: calc(#{$entity-list-max-height} + #{$search-input-height} + #{var(--spacing-200)});
    }
  }

  &::-webkit-scrollbar {
    width: 0.25rem;
  }

  &::-webkit-scrollbar-track {
    background: none;
    border-radius: 0.25rem;

    &:hover {
      background: var(--gray-96);
    }
  }

  &::-webkit-scrollbar-thumb {
    background: var(--gray-80);
    border-radius: 0.25rem;

    &:hover {
      background: var(--gray-80);
    }
  }

  .entity-item {
    margin-bottom: var(--spacing-50);

    &.active {
      background: var(--gray-96);
    }
  }
}

.seperator {
  margin: var(--spacing-25) 0 var(--spacing-50);
  border-color: var(--gray-94);
  height: 0;
}
</style>
